import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { PartialId, Word } from '@attain-apps/string-builder'

import type * as mosaicModels from '../models'

import type * as cytoscape from './cytoscape'
import * as models from './models'
import { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig(
  /**
   * __NOTE:__ we type this as potentially returning `undefined` because it is
   * not instantiated at first
   */
  manager: () => cytoscape.Manager | undefined,
) {
  return {
    /* -------------------------------------------------------------------------- */
    /*                                    Core                                    */
    /* -------------------------------------------------------------------------- */
    /* -------------------------------- Inserting ------------------------------- */
    addNode: store.actions.request({
      input: (
        params: models.Chart.Identifier & {
          readonly node?: Partial<ents.core.display.tree.Node>
        },
      ) => ({
        ...params,
        node: {
          ...params.node,
          values: {
            [ents.core.dms.data.Record.Id.KEY]: params.node
              ? ents.core.display.tree.Node.Id.get(params.node)
              : PartialId.from(Word.generate()),
          },
        },
        parentId: manager()?.getSelectedNodeId({
          chartId: params.chartId,
        }),
      }),
      output: store.actions.Data.asIs<{
        node: ents.core.display.tree.Node
      }>(),
      reducers: {
        request: ({ state, data: { chartId } }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart =>
              chart && {
                ...chart,
                isMutating: true,
                error: undefined,
              },
          }),
        error: ({
          state,
          data: {
            request: { chartId },
            error,
          },
        }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart =>
              chart && {
                ...chart,
                isMutating: false,
                error,
              },
          }),
        cancellation: ({
          state,
          data: {
            request: { chartId },
          },
        }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart =>
              chart && {
                ...chart,
                isMutating: false,
              },
          }),
        success: ({
          state,
          data: {
            request: { chartId: chartId, parentId },
            response: { node },
          },
        }) =>
          State.applyChangeToChart({
            change: models.Change.of({
              edges: {
                added: parentId
                  ? [
                      {
                        childId: ents.core.display.tree.Node.Id.get(node),
                        parentId,
                      },
                    ]
                  : undefined,
              },
              nodes: {
                added: [node],
              },
            }),
            chartId: chartId,
            state: State.patchChart({
              chartId,
              state,
              apply: chart =>
                chart && {
                  ...chart,
                  isMutating: false,
                },
            }),
          }),
      },
    }),
    putNode: store.actions.standalone({
      data: store.actions.Data.asIs<
        models.Chart.Identifier & {
          readonly node: ents.core.display.tree.Node
        }
      >(),
      reducer: ({ state, data: { chartId: orgChartId, node } }) =>
        State.applyChangeToChart({
          change: orgChart =>
            models.Change.of({
              nodes: {
                [orgChart?.nodes.some(
                  existingNode =>
                    ents.core.display.tree.Node.Id.get(existingNode) ===
                    ents.core.display.tree.Node.Id.get(node),
                )
                  ? 'edited'
                  : 'added']: [node],
              },
            }),
          chartId: orgChartId,
          state,
        }),
    }),
    /* --------------------------------- Editing -------------------------------- */
    editNode: store.actions.standalone({
      data: (
        params: NodeSelector & {
          readonly displayEdits?: models.DisplayEdits
          readonly edits?: Partial<ents.core.display.tree.Node['values']>
        },
      ) => ({
        ...params,
        id:
          params?.id ??
          manager()?.getSelectedNodeId({ chartId: params.chartId }),
      }),
      reducer: ({ state, data: { chartId, displayEdits, edits, id } }) =>
        id === undefined
          ? state
          : State.patchNodeEdit({
              apply:
                displayEdits || edits
                  ? (editedNode): ents.core.display.tree.Node => {
                      const nextNode: ents.core.display.tree.Node = {
                        ...editedNode,
                        ...displayEdits,
                      }

                      if (edits === undefined) {
                        return nextNode
                      }

                      return {
                        ...nextNode,
                        values: {
                          [ents.core.dms.data.Record.Id.KEY]:
                            editedNode.values?.[
                              ents.core.dms.data.Record.Id.KEY
                            ] ?? id,
                          ...(editedNode.values ?? {}),
                          ...edits,
                        },
                      }
                    }
                  : undefined,
              chartId,
              nodeId: id,
              state,
            }),
    }),
    editNodeValue: store.actions.standalone({
      data: (
        params: NodeSelector & {
          readonly attributeId: ents.core.dms.models.Attribute.Id
          readonly value: ents.core.dms.data.Value
        },
      ) => ({
        ...params,
        id:
          params?.id ??
          manager()?.getSelectedNodeId({ chartId: params.chartId }),
      }),
      reducer: ({ state }) => state,
    }),
    saveEdits: store.actions.request({
      input: store.actions.Data.asIs<models.Chart.Identifier>(),
      output: store.actions.Data.asIs<models.Chart.Identifier>(),
      reducers: {
        request: ({ state, data: { chartId } }) =>
          State.patchChart({
            apply: chart =>
              chart && {
                ...chart,
                error: undefined,
                isMutating: true,
              },
            chartId,
            state,
          }),
        success: ({
          state,
          data: {
            request: { chartId },
          },
        }) => {
          state = State.applyChangeToChart({
            change: chart => {
              const nodeId = chart?.currentlyEditingNodeId
              if (!nodeId) {
                return
              }
              const to = chart?.edits[nodeId]
              if (!to) {
                return
              }
              const from = chart?.nodes.find(
                node => ents.core.display.tree.Node.Id.get(node) === nodeId,
              )
              if (!from) {
                return
              }
              return models.Change.of({
                nodes: { edited: [{ from, to }] },
              })
            },
            chartId,
            state: State.patchChart({
              apply: chart =>
                chart && {
                  ...chart,
                  isMutating: false,
                },
              chartId,
              state,
            }),
          })
          // NOTE: the above function makes this mutable
          delete state.charts[chartId]?.currentlyEditingNodeId
          return state
        },
        error: ({
          state,
          data: {
            request: { chartId },
          },
        }) =>
          State.patchChart({
            apply: chart => chart && { ...chart, isMutating: false },
            chartId,
            state,
          }),
        cancellation: ({
          state,
          data: {
            request: { chartId },
          },
        }) =>
          State.patchChart({
            apply: chart => chart && { ...chart, isMutating: false },
            chartId,
            state,
          }),
      },
    }),
    cancelEdits: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
      reducer: ({ state, data: { chartId: orgChartId } }) =>
        State.patchChart({
          apply: chart =>
            chart && {
              ...chart,
              currentlyEditingNodeId: undefined,
            },
          chartId: orgChartId,
          state,
        }),
    }),
    /* ------------------------------- File Upload ------------------------------ */
    uploadFile: store.actions.request({
      input: store.actions.Data.asIs<{
        readonly file: File
        readonly chartId: models.Chart.Id
      }>(),
      output: store.actions.Data.NONE,
      reducers: {
        request: ({ state }) => ({
          ...state,
          isUploadingResumeFile: true,
        }),
        success: ({ state }) => ({
          ...state,
          isUploadingResumeFile: false,
        }),
        error: ({ state }) => ({
          ...state,
          isUploadingResumeFile: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isUploadingResumeFile: false,
        }),
      },
    }),
    /* -------------------------------- Deletion -------------------------------- */
    deleteNode: store.actions.request({
      input: (params: NodeSelector) => ({
        ...params,
        id:
          params?.id ??
          manager()?.getSelectedNodeId({ chartId: params.chartId }),
      }),
      output: store.actions.Data.asIs<NodeSelector>(),
      reducers: {
        request: ({ state, data: { chartId } }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart =>
              chart && { ...chart, isMutating: true, error: undefined },
          }),
        success: ({
          state,
          data: {
            request: { chartId, id },
          },
        }) =>
          id === undefined
            ? state
            : State.applyChangeToChart({
                change: orgChart => {
                  const nodesToDelete = orgChart?.nodes.filter(
                    node => ents.core.display.tree.Node.Id.get(node) === id,
                  )
                  const edgesToDelete = orgChart?.edges.filter(
                    edge => edge.parentId === id || edge.childId === id,
                  )
                  return models.Change.of({
                    edges: { deleted: edgesToDelete },
                    nodes: { deleted: nodesToDelete },
                  })
                },
                chartId,
                state: State.patchChart({
                  chartId,
                  state,
                  apply: chart => chart && { ...chart, isMutating: false },
                }),
              }),
        error: ({
          state,
          data: {
            request: { chartId },
            error,
          },
        }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart => chart && { ...chart, isMutating: false, error },
          }),
        cancellation: ({
          state,
          data: {
            request: { chartId },
          },
        }) =>
          State.patchChart({
            chartId,
            state,
            apply: chart => chart && { ...chart, isMutating: false },
          }),
      },
    }),
    /* ------------------------------- Assignment ------------------------------- */
    assign: store.actions.standalone({
      data: store.actions.Data.asIs<
        models.Chart.Identifier & {
          readonly edge: models.Edge
        }
      >(),
      reducer: ({ state, data: { chartId: orgChartId, edge } }) => {
        if (models.Chart.Entry.get(orgChartId) === 'current') {
          return state
        }

        return State.applyChangeToChart({
          change: orgChart => {
            const nextEdge: models.Edge = {
              ...edge,
              leaderType:
                edge.leaderType ?? ents.extra.common.LeaderType.Manager,
            }

            if (
              orgChart?.edges.some(
                existingEdge =>
                  existingEdge.parentId === nextEdge.parentId &&
                  existingEdge.childId === nextEdge.childId,
              )
            ) {
              return undefined
            }

            return models.Change.of({
              edges: {
                added: [nextEdge],
                deleted: orgChart?.edges.filter(existingEdge => {
                  const existingLeaderType =
                    existingEdge.leaderType ??
                    ents.extra.common.LeaderType.Manager

                  if (
                    existingEdge.childId === nextEdge.childId &&
                    existingLeaderType === nextEdge.leaderType
                  ) {
                    // Reassigns only the targeted typed relationship.
                    return true
                  }
                  if (
                    nextEdge.leaderType ===
                      ents.extra.common.LeaderType.Manager &&
                    existingLeaderType ===
                      ents.extra.common.LeaderType.Manager &&
                    existingEdge.parentId === nextEdge.childId
                  ) {
                    const visited = new Set<string>()
                    // Removes primary manager descendants that would cause a
                    // cycle with the new primary manager edge.
                    const descendants = [existingEdge]
                    let descendant: models.Edge | undefined
                    while ((descendant = descendants.pop())) {
                      if (descendant.childId === nextEdge.parentId) {
                        return true
                      }
                      visited.add(descendant.childId)
                      descendants.push(
                        ...orgChart.edges.filter(potentialDescendant => {
                          const potentialLeaderType =
                            potentialDescendant.leaderType ??
                            ents.extra.common.LeaderType.Manager
                          return (
                            potentialLeaderType ===
                              ents.extra.common.LeaderType.Manager &&
                            potentialDescendant.parentId ===
                              descendant?.childId &&
                            !visited.has(potentialDescendant.childId)
                          )
                        }),
                      )
                    }
                  }
                  // Doesn't remove anything else
                  return false
                }),
              },
            })
          },
          chartId: orgChartId,
          state,
        })
      },
    }),
    /* -------------------------------- Undo/Redo ------------------------------- */
    undo: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
      reducer: ({ state, data: { chartId: orgChartId } }) => {
        if (models.Chart.Entry.get(orgChartId) === 'current') {
          return state
        }
        return State.moveThroughHistoryInOrgChart({
          steps: 1,
          chartId: orgChartId,
          state,
        })
      },
    }),
    redo: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
      reducer: ({ state, data: { chartId: orgChartId } }) => {
        if (models.Chart.Entry.get(orgChartId) === 'current') {
          return state
        }
        return State.moveThroughHistoryInOrgChart({
          steps: -1,
          chartId: orgChartId,
          state,
        })
      },
    }),
    /* -------------------------------- Clean Up -------------------------------- */
    clear: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
      reducer: ({ state, data: { chartId: orgChartId } }) =>
        State.applyChangeToChart({
          change: orgChart =>
            orgChart &&
            models.Change.of({
              edges: {
                deleted: orgChart.edges,
              },
              nodes: {
                deleted: orgChart.nodes,
              },
            }),
          chartId: orgChartId,
          state,
        }),
    }),
    cleanUp: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
    }),
    /* -------------------------------------------------------------------------- */
    /*                                  Proposals                                 */
    /* -------------------------------------------------------------------------- */
    propose: store.actions.standalone({
      data: store.actions.Data.asIs<
        | ({
            data: models.Proposal.ParseResult
            source: 'assistant'
          } & models.Chart.Identifier<string, 'proposed'>)
        | models.Chart.Id<string, 'current'>
      >(),
      reducer: ({ state, data }) => {
        if (models.Chart.Id.isId(data)) {
          const [widgetRefId] = models.Chart.Id.getParts(data)
          return {
            ...state,
            charts: {
              ...state.charts,
              [models.Chart.Id.from({ widgetRefId, entry: 'proposed' })]: {
                ...state.charts[data],
                error: undefined,
              },
            },
          }
        } else {
          const [widgetRefId] = models.Chart.Id.getParts(data.chartId)
          const currentChartId = models.Chart.Id.from(widgetRefId)
          const currentChart = state.charts[currentChartId]
          const existingProposalChart = state.charts[data.chartId]

          if (!currentChart) {
            return state
          }

          const proposalData = data.data.ok
            ? models.Proposal.toData({
                currentChart,
                proposal: data.data.data,
              })
            : data.data

          if (!proposalData.ok) {
            return {
              ...state,
              charts: {
                ...state.charts,
                [data.chartId]: _createProposalErrorChart({
                  currentChart,
                  error: proposalData.error,
                  existingProposalChart,
                }),
              },
            }
          }

          return {
            ...state,
            charts: {
              ...state.charts,
              [currentChartId]: {
                ...currentChart,
                error: undefined,
              },
              [data.chartId]: _createProposalChart({
                currentChart,
                proposalData: proposalData.data,
              }),
            },
          }
        }
      },
    }),
    approveProposal: store.actions.request({
      input: store.actions.Data.asIs<{
        widgetRefId: mosaicModels.widget.Ref.Id
      }>(),
      output: store.actions.Data.asIs<models.ApproveProposal.Response>(),
      reducers: {
        request: ({ state, data: { widgetRefId } }) =>
          State.patchChart({
            chartId: models.Chart.Id.from({
              entry: 'proposed',
              widgetRefId,
            }),
            state,
            apply: chart =>
              chart && {
                ...chart,
                error: undefined,
                isMutating: true,
              },
          }),
        success: ({
          state,
          data: {
            request: { widgetRefId },
            response: { approvedEdges },
          },
        }) => {
          const proposedId = models.Chart.Id.from({
            widgetRefId,
            entry: 'proposed',
          })
          const currentId = models.Chart.Id.from(widgetRefId)

          const { [currentId]: current, [proposedId]: proposed } = state.charts
          if (!proposed || !current) {
            return state
          }
          state = State.applyChangeToChart({
            change: models.Change.of({
              edges: {
                added: approvedEdges,
                deleted: current.edges,
              },
              nodes: {
                added: proposed.nodes,
                deleted: current.nodes,
              },
            }),
            chartId: currentId,
            state,
          })

          state = State.patchChart({
            chartId: currentId,
            state,
            apply: chart =>
              chart && {
                ...chart,
                error: undefined,
                isMutating: false,
              },
          })

          // Deletes the proposal, since now it has been applied to the current
          // org chart
          state.charts = {
            ...state.charts,
            [proposedId]: undefined,
          }
          return state
        },
        error: ({
          state,
          data: {
            request: { widgetRefId },
            error,
          },
        }) =>
          State.patchChart({
            chartId: models.Chart.Id.from({
              entry: 'proposed',
              widgetRefId,
            }),
            state,
            apply: chart =>
              chart && {
                ...chart,
                error,
                isMutating: false,
              },
          }),
        cancellation: ({
          state,
          data: {
            request: { widgetRefId },
            reason,
          },
        }) =>
          State.patchChart({
            chartId: models.Chart.Id.from({
              entry: 'proposed',
              widgetRefId,
            }),
            state,
            apply: chart =>
              chart && {
                ...chart,
                error: reason,
                isMutating: false,
              },
          }),
      },
    }),
    rejectProposal: store.actions.standalone({
      data: store.actions.Data.asIs<{
        widgetRefId: mosaicModels.widget.Ref.Id
      }>(),
      reducer: ({ state, data: { widgetRefId } }) => ({
        ...state,
        charts: {
          ...state.charts,
          [models.Chart.Id.from({
            widgetRefId,
            entry: 'proposed',
          })]: undefined,
        },
      }),
    }),
    /* -------------------------------------------------------------------------- */
    /*                             Instance Management                            */
    /* -------------------------------------------------------------------------- */
    bind: store.actions.standalone({
      data: store.actions.Data.asIs<models.Binding>(),
    }),
    destroy: store.actions.standalone({
      data: store.actions.Data.asIs<models.Chart.Identifier>(),
    }),
    /* ------------------------------- Initialized ------------------------------ */
    init: store.actions.standalone({
      data: store.actions.Data.asIs<{
        tree: ents.core.display.tree.Tree
        widgetRefId: mosaicModels.widget.Ref.Id
      }>(),
      reducer: ({ state, data: { tree, widgetRefId } }) =>
        State.init({ state, tree, widgetRefId }),
    }),
    loadEdges: store.actions.request({
      input: store.actions.Data.asIs<
        Pick<ents.core.display.tree.Tree, 'edgesFrom'> & {
          chartId: models.Chart.Id
        }
      >(),
      output: store.actions.Data.asIs<{ edges: readonly models.Edge[] }>(),
      reducers: {
        success: ({
          state,
          data: {
            request: { chartId: chartId },
            response: { edges },
          },
        }) => {
          const oldChart = state.charts[chartId]
          return State.patchChart({
            apply: {
              areEdgesLoaded: true,
              edges,
              edits: {},
              history: [],
              metadata: oldChart?.metadata ?? {},
              nodes: oldChart?.nodes ?? [],
              peopleInsightsLookup: {},
              undoCount: 0,
            },
            chartId,
            state,
          })
        },
      },
    }),
    loadPeopleInsights: store.actions.request({
      input: store.actions.Data.asIs<NodeSelector>(),
      output: store.actions.Data.asIs<{
        peopleInsightsById: Partial<
          Record<
            ents.core.display.tree.Node.Id,
            ents.core.display.tree.Node.PeopleInsights
          >
        >
        failures: readonly ents.core.display.tree.Node.Id[]
      }>(),
      reducers: {
        success: ({
          state,
          data: {
            request: { chartId },
            response: { peopleInsightsById },
          },
        }) =>
          State.patchChart({
            apply: chart => {
              if (!chart) {
                return
              }
              return {
                ...chart,
                peopleInsightsLookup: {
                  ...chart.peopleInsightsLookup,
                  ...peopleInsightsById,
                },
              }
            },
            chartId,
            state,
          }),
      },
    }),
    /* -------------------------- Remote Storage (dms) -------------------------- */
    saveData: store.actions.request({
      input: store.actions.Data.asIs<{
        chartId?: models.Chart.Id
        record: ents.core.dms.data.Record
        modelId: ents.core.dms.models.ForRecord.Id
      }>(),
      output: store.actions.Data.asIs<void>(),
    }),
    deleteData: store.actions.request({
      input: store.actions.Data.asIs<{
        chartId?: models.Chart.Id
        recordId: ents.core.dms.data.Record.Id
        modelId: ents.core.dms.models.ForRecord.Id
      }>(),
      output: store.actions.Data.asIs<void>(),
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}

type NodeSelector = models.Chart.Identifier & {
  /**
   * @default currently selected ID
   */
  readonly id?: ents.core.display.tree.Node.Id
}

/** Creates the proposed chart state entry from validated proposal data. */
function _createProposalChart({
  currentChart,
  proposalData,
}: {
  currentChart: models.Chart
  proposalData: models.Data
}): models.Chart {
  return {
    areEdgesLoaded: true,
    edges: proposalData.edges,
    edits: {},
    error: undefined,
    history: [],
    metadata: currentChart.metadata,
    nodes: proposalData.nodes,
    peopleInsightsLookup: currentChart.peopleInsightsLookup,
    undoCount: 0,
  }
}

/**
 * Creates proposal-chart state for assistant proposal failures so the proposal
 * view can surface the latest error instead of leaving stale content visible.
 */
function _createProposalErrorChart({
  currentChart,
  error,
  existingProposalChart,
}: {
  currentChart: models.Chart
  error: models.Chart['error']
  existingProposalChart?: models.Chart
}): models.Chart {
  return {
    ...(existingProposalChart ?? currentChart),
    error,
    isMutating: false,
  }
}
